// Renders a captured patient consent signature.
//
// The backend stores `signature_image` as a base64 PNG (already a data URL
// when prefixed) and `consent_typed_name` for typed-name consent. This
// component handles both cases and shows the consent metadata for audit.

export default function SignatureViewer({ payment }) {
  if (!payment) return null;
  const {
    consent_method,
    consent_typed_name,
    consent_text,
    signature_image,
    signature_hash,
    consented_at,
    consent_ip,
  } = payment;

  if (!consent_method) {
    return (
      <div className="text-xs text-slate-500 italic">No consent on file for this payment.</div>
    );
  }

  const dataUrl = signature_image
    ? (signature_image.startsWith('data:') ? signature_image : `data:image/png;base64,${signature_image}`)
    : null;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs uppercase tracking-wide text-slate-500">Consent</span>
        <span className="px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800">
          {consent_method === 'drawn' ? 'Signed on screen' : 'Typed name'}
        </span>
        {consented_at && (
          <span className="text-xs text-slate-500">
            {new Date(consented_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        )}
      </div>

      {consent_method === 'drawn' && dataUrl && (
        <div className="border border-slate-200 rounded bg-white p-2 inline-block">
          <img
            src={dataUrl}
            alt="Patient signature"
            className="max-h-32 max-w-xs object-contain"
          />
        </div>
      )}
      {consent_method === 'typed' && consent_typed_name && (
        <div className="border border-slate-200 rounded bg-white p-3 inline-block">
          <div className="text-xs text-slate-500 mb-1">Typed name:</div>
          <div className="font-serif italic text-2xl text-slate-800">{consent_typed_name}</div>
        </div>
      )}

      {consent_text && (
        <details className="text-xs text-slate-600">
          <summary className="cursor-pointer hover:text-slate-800">Consent text shown to patient</summary>
          <p className="mt-1 whitespace-pre-wrap p-2 bg-slate-50 border border-slate-100 rounded">{consent_text}</p>
        </details>
      )}

      {(signature_hash || consent_ip) && (
        <div className="text-[11px] text-slate-400 font-mono space-y-0.5 pt-1 border-t border-slate-100">
          {signature_hash && <div>SHA-256: {signature_hash.slice(0, 32)}…</div>}
          {consent_ip   && <div>IP: {consent_ip}</div>}
        </div>
      )}
    </div>
  );
}
